import React, { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react'
import { useUIStore } from '@/stores/uiStore'
import { useIsMobile } from '@/hooks/useIsMobile'
import { SafeAreaBottom } from './SafeAreaBottom'

// MIDNIGHT pastel tints per severity
const TINTS = {
  success: '#A8E6CE', // mint
  error:   '#F4B5BD', // blush
  warning: '#F3DF93', // butter
  info:    '#B5D3F0', // sky
  default: null,      // neutral glass
} as const

type ToastType = 'success' | 'error' | 'warning' | 'info'

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `${r},${g},${b}`
}

interface ToastItemProps {
  id: string
  type: ToastType
  title: string
  message?: string
  duration?: number
  action?: { label: string; onClick: () => void }
  onRemove: (id: string) => void
}

function ToastItem({ id, type, title, message, duration = 4000, action, onRemove }: ToastItemProps) {
  const [progress, setProgress] = useState(100)
  const tint = TINTS[type]

  // prefers-reduced-motion is handled via CSS animation on the wrapper
  useEffect(() => {
    const interval = 50
    const step = (interval / duration) * 100
    const timer = setInterval(() => {
      setProgress(p => {
        if (p <= 0) { clearInterval(timer); return 0 }
        return p - step
      })
    }, interval)
    return () => clearInterval(timer)
  }, [duration])

  // Severity icon chip style: tint with 0.14 opacity bg
  const iconChipStyle: React.CSSProperties = tint
    ? {
        background: `rgba(${hexToRgb(tint)},0.14)`,
        borderRadius: 9999,
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }
    : {
        display: 'flex',
        alignItems: 'flex-start',
        paddingTop: 2,
        flexShrink: 0,
      }

  const iconColor: React.CSSProperties = { color: tint ?? 'rgba(255,255,255,0.60)' }

  const icons: Record<ToastType, React.ElementType> = {
    success: CheckCircle,
    error:   XCircle,
    warning: AlertTriangle,
    info:    Info,
  }
  const Icon = icons[type]

  return (
    <div
      className="toast-item"
      style={{
        position: 'relative',
        background: 'var(--midnight-tile, rgba(255,255,255,0.04))',
        backdropFilter: 'blur(32px) saturate(180%)',
        WebkitBackdropFilter: 'blur(32px) saturate(180%)',
        border: '0.5px solid var(--midnight-border, rgba(255,255,255,0.08))',
        borderRadius: 'var(--midnight-r-md, 18px)',
        boxShadow: 'var(--midnight-shadow-lift, 0 20px 50px -12px rgba(0,0,0,0.7), 0 4px 12px rgba(0,0,0,0.5))',
        overflow: 'hidden',
        padding: '14px 44px 14px 14px',
        width: '100%',
        minWidth: 280,
        maxWidth: 360,
      }}
      role="alert"
      aria-live="polite"
    >
      {/* Top-edge highlight sheen */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: '0 0 auto 0',
          height: 1,
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)',
          pointerEvents: 'none',
        }}
      />

      {/* Dismiss button */}
      <button
        onClick={() => onRemove(id)}
        className="absolute top-2 right-2 p-2 hover:text-white transition-colors rounded-lg"
        style={{
          minWidth: 44,
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--midnight-text3, rgba(255,255,255,0.40))',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
        aria-label="Dismiss notification"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Content row */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        {/* Icon chip */}
        <div style={iconChipStyle}>
          <Icon style={{ width: 16, height: 16, ...iconColor }} />
        </div>

        {/* Text + action */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1.3,
              color: tint ?? 'var(--midnight-text, #FFFFFF)',
              letterSpacing: '-0.012em',
            }}
          >
            {title}
          </p>
          {message && (
            <p
              style={{
                margin: '3px 0 0',
                fontSize: 13,
                lineHeight: 1.45,
                color: 'var(--midnight-text2, rgba(255,255,255,0.60))',
              }}
            >
              {message}
            </p>
          )}
          {action && (
            <button
              onClick={() => { action.onClick(); onRemove(id) }}
              style={{
                marginTop: 8,
                display: 'inline-flex',
                alignItems: 'center',
                height: 24,
                padding: '0 10px',
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 9999,
                background: tint ? `rgba(${hexToRgb(tint)},0.14)` : 'var(--midnight-tile, rgba(255,255,255,0.04))',
                border: tint
                  ? `0.5px solid rgba(${hexToRgb(tint)},0.28)`
                  : '0.5px solid var(--midnight-border, rgba(255,255,255,0.08))',
                color: tint ?? 'var(--midnight-text2, rgba(255,255,255,0.60))',
                cursor: 'pointer',
              }}
            >
              {action.label}
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 2,
          background: 'rgba(255,255,255,0.06)',
          borderRadius: '0 0 var(--midnight-r-md, 18px) var(--midnight-r-md, 18px)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${progress}%`,
            background: tint ?? 'rgba(255,255,255,0.3)',
            transition: 'width 50ms linear',
          }}
        />
      </div>
    </div>
  )
}

export function ToastContainer() {
  const { toasts, removeToast } = useUIStore()
  const isMobile = useIsMobile()

  if (toasts.length === 0) return null

  return (
    <>
      {/* Slide-in animation — respects prefers-reduced-motion */}
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          .toast-item {
            animation: toast-slide-in 0.3s cubic-bezier(0.2, 0.8, 0.2, 1) both;
          }
          @keyframes toast-slide-in {
            from { opacity: 0; transform: translateX(20px); }
            to   { opacity: 1; transform: translateX(0); }
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .toast-item {
            animation: toast-fade-in 0.2s ease both;
          }
          @keyframes toast-fade-in {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
        }
      `}</style>
      {isMobile ? (
        // Mobile: SafeAreaBottom owns env(safe-area-inset-bottom); extra=49+12px
        // keeps toast clear of the bottom tab bar (49px) + 0.75rem (12px) gap.
        <div className="fixed z-[100] left-4 right-4 bottom-0 pointer-events-none">
          <SafeAreaBottom extra="calc(49px + 0.75rem)" className="flex flex-col gap-2 pointer-events-auto">
            {toasts.map((toast) => (
              <ToastItem key={toast.id} {...toast} onRemove={removeToast} />
            ))}
          </SafeAreaBottom>
        </div>
      ) : (
        <div
          className="fixed z-[100] flex flex-col gap-2 left-4 right-4 md:left-auto md:right-4 md:w-auto"
          style={{ bottom: '0.75rem' }}
        >
          {toasts.map((toast) => (
            <ToastItem key={toast.id} {...toast} onRemove={removeToast} />
          ))}
        </div>
      )}
    </>
  )
}
